import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

const styles = `
  .signin-logo {
    will-change: transform, filter;
    animation: logo-breathe 6s ease-in-out infinite;
  }
  @keyframes logo-breathe {
    0%, 100% { filter: drop-shadow(0 0 8px rgba(241, 90, 41, 0.25)); }
    50% { filter: drop-shadow(0 0 22px rgba(241, 90, 41, 0.5)); }
  }
  .signin-blob { will-change: transform; }
  .signin-blob-1 { top: -8rem; left: -6rem; animation: drift-1 37s ease-in-out infinite; }
  .signin-blob-2 { bottom: -10rem; right: -8rem; animation: drift-2 43s ease-in-out infinite; }
  .signin-blob-3 { top: 40%; left: 55%; animation: drift-3 53s ease-in-out infinite; }
  @keyframes drift-1 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    25% { transform: translate(14rem, 9rem) scale(1.18); }
    50% { transform: translate(5rem, 22rem) scale(0.95); }
    75% { transform: translate(20rem, 13rem) scale(1.1); }
  }
  @keyframes drift-2 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    25% { transform: translate(-12rem, -16rem) scale(1.12); }
    50% { transform: translate(-22rem, -4rem) scale(0.92); }
    75% { transform: translate(-6rem, -12rem) scale(1.2); }
  }
  @keyframes drift-3 {
    0%, 100% { transform: translate(0, 0) scale(0.95); }
    25% { transform: translate(-18rem, 8rem) scale(1.2); }
    50% { transform: translate(-9rem, -14rem) scale(1.05); }
    75% { transform: translate(-24rem, -3rem) scale(0.9); }
  }
  .signin-content { transition: transform 0.6s ease; }
  .signin-content:hover { transform: scale(1.03); }
  .signin-breathe { animation: breathe 6s ease-in-out infinite; }
  @keyframes breathe {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.02); }
  }
`;

const MicrosoftIcon = () => (
  <svg
    className="h-5 w-5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 11.5V5.5a1.5 1.5 0 0 1 3 0v5" />
    <path d="M12 10.5v-1a1.5 1.5 0 0 1 3 0v1.5" />
    <path d="M15 11v-.5a1.5 1.5 0 0 1 3 0V14a6 6 0 0 1-6 6h-1.5a6 6 0 0 1-4.243-1.757l-2.6-2.6a1.5 1.5 0 0 1 2.122-2.122L9 15.5" />
  </svg>
);

const LoginScreen = () => {
  const { login, error } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);

  const handleSignIn = () => {
    setIsSigningIn(true);
    login();
  };

  return (
    <div
      onClick={isSigningIn ? undefined : handleSignIn}
      className="relative flex min-h-screen cursor-pointer select-none flex-col items-center justify-center overflow-hidden bg-white p-4"
    >
      <style>{styles}</style>

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="signin-blob signin-blob-1 absolute h-[34rem] w-[34rem] rounded-full bg-brand-300/30 blur-3xl" />
        <div className="signin-blob signin-blob-2 absolute h-[28rem] w-[28rem] rounded-full bg-amber-200/40 blur-3xl" />
        <div className="signin-blob signin-blob-3 absolute h-[24rem] w-[24rem] rounded-full bg-brand-200/30 blur-3xl" />
      </div>

      <div className="signin-content">
        <div className="signin-breathe relative flex flex-col items-center gap-10">
          <h1 className="signin-logo text-5xl font-bold tracking-tight sm:text-6xl">
            <span className="text-brand-500">Duo</span>{' '}
            <span className="text-neutral-800">Documentation</span>
          </h1>

          <div className="flex items-center gap-2 rounded-full bg-white/50 px-6 py-2.5 text-lg font-medium text-neutral-500">
            {isSigningIn ? (
              <>
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-300 border-t-brand-500" />
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <MicrosoftIcon />
                <span>Click to sign in</span>
              </>
            )}
          </div>

          {error && (
            <div className="rounded-xl bg-red-50/80 px-6 py-3 text-red-600 backdrop-blur-sm">{error}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
